import { Box, Button, Tooltip, useTheme } from "@mui/material";
import { prettyPrintEnumValue } from "encamp-shared/src/utils/prettyPrintEnumValue";
import {
  ActivityStatus,
  CredentialVerificationStatus,
  GetCredentialQuery,
} from "generated-graphql/graphql";
import { useCreateTierIIReportComment } from "hooks/comments";
import { useActivityLogs } from "hooks/useActivityLogs";
import { useVerifyCredential } from "hooks/useVerifyCredentials";
import { relativeLogTime } from "util/dates";
import { FieldDisplay } from "./shared/FieldComponents";
import { useReportDetails } from "./useReportDetails";

const RELATIVE_TIME = 12;
const NEVER_CHECKED = "Never Checked";

export const CredentialVerification = ({
  credential,
  facilityId,
  credentialVerificationAvailable = false,
  disabled = false,
}: {
  credential: GetCredentialQuery["credential"] | undefined;
  facilityId: string;
  credentialVerificationAvailable?: boolean;
  disabled?: boolean;
}) => {
  const theme = useTheme();
  const { data } = useReportDetails();
  const [verifyCredential, { loading: sendingCredentialVerification }] =
    useVerifyCredential();
  const [createComment] = useCreateTierIIReportComment();

  // Get current activity and check if it's in error
  const currentActivity = data?.tierIIReport.currentWorkflow?.activities.find(
    (act) => act.id === data?.tierIIReport.currentWorkflow?.currentActivityId
  );
  const isActivityInError = currentActivity?.status === ActivityStatus.Error;

  // Get error message from activity logs if activity is in error
  const { data: logData } = useActivityLogs(
    currentActivity?.id,
    isActivityInError ? `status:${ActivityStatus.Error}` : "",
    0,
    1,
    [{ field: "time", sort: "desc" }]
  );

  const errorMessage =
    isActivityInError && logData?.activityLogs.items?.[0]?.message
      ? logData.activityLogs.items[0].message
      : null;

  const workflowId = data?.tierIIReport.currentWorkflow?.id;
  const reportId = data?.tierIIReport.id;

  const handleVerifyCredential = async () => {
    await verifyCredential({
      variables: {
        input: {
          facilityId: facilityId,
          credentialId: credential?.id,
        },
      },
      refetchQueries: ["GetTierIIReport", "GetCredential"],
    });

    // Add workflow comment
    if (workflowId && reportId) {
      let commentText = "Verified credential";
      if (errorMessage) {
        commentText += `:\n"${errorMessage}"`;
      }

      await createComment({
        variables: {
          input: {
            workflowId,
            reportId,
            comment: commentText,
          },
        },
      });
    }
  };

  const verificationTime = credential?.lastVerificationCheckAt;
  const lastSuccessfulVerificationTime = credential?.lastVerificationSuccessAt;

  return (
    <Box
      sx={{ display: "flex", flexDirection: "column", gap: theme.spacing(1) }}
    >
      <FieldDisplay
        label="Verification Status:"
        value={prettyPrintEnumValue(credential?.verificationStatus)}
        noWrap
      />
      <FieldDisplay
        label="Last Check:"
        value={
          <Tooltip title={verificationTime}>
            <span>
              {verificationTime
                ? relativeLogTime(verificationTime, RELATIVE_TIME)
                : NEVER_CHECKED}
            </span>
          </Tooltip>
        }
        noWrap
      />
      <FieldDisplay
        label={
          <>
            Last Successful
            <br />
            Verification Check:
          </>
        }
        value={
          <Tooltip title={lastSuccessfulVerificationTime}>
            <span>
              {lastSuccessfulVerificationTime
                ? relativeLogTime(lastSuccessfulVerificationTime, RELATIVE_TIME)
                : NEVER_CHECKED}
            </span>
          </Tooltip>
        }
        alignItems="flex-start"
        noWrap
      />
      <Box paddingTop={theme.spacing(1)}>
        <Tooltip
          title={
            !credentialVerificationAvailable
              ? "Credential verification is not supported for this portal"
              : ""
          }
        >
          <span>
            <Button
              disabled={
                disabled ||
                !credentialVerificationAvailable ||
                credential?.verificationStatus ===
                  CredentialVerificationStatus.CurrentlyVerifying ||
                !!sendingCredentialVerification
              }
              variant="outlined"
              onClick={handleVerifyCredential}
              size="small"
            >
              Verify Credential
            </Button>
          </span>
        </Tooltip>
      </Box>
    </Box>
  );
};
